import APIService from "./APIService";

/**
 * API service for User Scam Reports.
 *
 * Architecture:
 *   saveReport()  — called immediately when a scam is detected (saves to user_scam_reports)
 *   shareReport() — called when the user shares a saved report with family (creates user_scam_report_shares)
 *
 * This is separate from AlertIntelligenceAPI (which handles AIH alerts).
 */
class ScamReportAPI {
  static BASE_PATH = "/api/v1/scam/reports";

  /**
   * Save a scam detection result immediately when detected.
   * Called automatically by all scam-detector flows when isScam=true.
   *
   * @param {Object} params
   * @param {string} params.analysisType    - e.g. "URL_SAFETY", "TEXT_ANALYSIS"
   * @param {string} [params.analysisTitle] - Human-readable title
   * @param {string} [params.analysisDetail]- Key detail (URL, phone number, etc.)
   * @param {string} [params.analysisInput] - Full user input
   * @param {boolean} [params.isScam]       - Whether a scam was detected
   * @param {number} [params.confidence]    - AI confidence 0–1
   * @param {string} [params.riskLevel]     - LOW, MEDIUM, HIGH, CRITICAL
   * @param {string} [params.explanation]   - AI explanation
   * @param {string} [params.suggestions]   - AI suggestions
   * @returns {Promise<Object>} { success, reportId, analysisTitle, ... }
   */
  static async saveReport(params) {
    try {
      const response = await APIService.post(ScamReportAPI.BASE_PATH, {
        analysisType: params.analysisType,
        analysisTitle: params.analysisTitle || null,
        analysisDetail: params.analysisDetail || null,
        analysisInput: params.analysisInput || null,
        context: params.context || null,
        isScam: params.isScam ?? null,
        confidence: params.confidence ?? null,
        riskLevel: params.riskLevel || null,
        explanation: params.explanation || null,
        suggestions: Array.isArray(params.suggestions)
          ? params.suggestions.join("\n")
          : params.suggestions || null,
        sourceAnalysisId: params.sourceAnalysisId ?? null,
        sourceAnalysisType: params.sourceAnalysisType || null,
      });
      return response;
    } catch (error) {
      console.error("ScamReportAPI: Error saving report:", error);
      throw error;
    }
  }

  /**
   * Share an existing scam report with the user's family group.
   * The report must already be saved via saveReport().
   *
   * @param {number} reportId - ID of the saved scam report
   * @param {string} [message] - Optional personal message to family
   * @returns {Promise<Object>} { success, reportId, shareId, recipientNames, ... }
   */
  static async shareReport(reportId, message = null) {
    try {
      const response = await APIService.post(
        `${ScamReportAPI.BASE_PATH}/${reportId}/share`,
        { message: message || null },
      );
      return response;
    } catch (error) {
      console.error("ScamReportAPI: Error sharing report:", error);
      throw error;
    }
  }

  /**
   * Get the authenticated user's scam report history.
   *
   * @param {number} [page=0] - Page number (0-based)
   * @param {number} [size=20] - Page size
   * @returns {Promise<Object>} { reports, totalElements, totalPages, currentPage }
   */
  static async getUserReports(page = 0, size = 20) {
    try {
      const response = await APIService.get(
        `${ScamReportAPI.BASE_PATH}?page=${page}&size=${size}`,
      );
      return response;
    } catch (error) {
      console.error("ScamReportAPI: Error fetching reports:", error);
      throw error;
    }
  }

  /**
   * Get a single scam report by ID.
   *
   * @param {number} reportId
   * @returns {Promise<Object>} The scam report
   */
  static async getReport(reportId) {
    try {
      const response = await APIService.get(
        `${ScamReportAPI.BASE_PATH}/${reportId}`,
      );
      return response;
    } catch (error) {
      console.error("ScamReportAPI: Error fetching report:", error);
      throw error;
    }
  }

  /**
   * Look up scam report IDs from domain-specific analysis IDs.
   *
   * Used by history panels (SMS, Image, Social Media) to resolve which
   * history items already have a saved scam report, enabling the Share button.
   *
   * @param {string} sourceAnalysisType - e.g. "SMS_ANALYSIS", "IMAGE_ANALYSIS", "SOCIAL_MEDIA_ANALYSIS"
   * @param {number[]} sourceAnalysisIds - Array of domain entity PKs
   * @returns {Promise<Object>} { mapping: { sourceAnalysisId: reportId, ... } }
   */
  static async lookupReportIdsBySource(sourceAnalysisType, sourceAnalysisIds) {
    try {
      if (!sourceAnalysisIds || sourceAnalysisIds.length === 0) {
        return { mapping: {} };
      }
      const params = new URLSearchParams();
      params.append("sourceAnalysisType", sourceAnalysisType);
      sourceAnalysisIds.forEach((id) => params.append("sourceAnalysisIds", id));
      const response = await APIService.get(
        `${ScamReportAPI.BASE_PATH}/lookup-by-source?${params.toString()}`,
      );
      return response;
    } catch (error) {
      console.error("ScamReportAPI: Error looking up report IDs:", error);
      return { mapping: {} };
    }
  }
}

export default ScamReportAPI;
